import React, { useEffect, useRef, useState } from "react";
import styles from "./index.module.css";
import Button from "shared/Button";

/**
 * Takes a file as `value` and passes a new image file to `onChange` when picked.
 * The `label` is text to be shown on the button that triggers the file choose dialog.
 * The `defaultValue` is the image displayed if `value` is null.
 */
export default function ImagePicker({
  value,
  defaultValue,
  backgroundColor,
  onChange,
  label,
}) {
  const thumbnail = useRef(null);
  const [preview, setPreview] = useState(null);
  const [fileError, setFileError] = useState("");

  useEffect(() => {
    if (!value) {
      setPreview(null);
      return;
    }

    const url = URL.createObjectURL(value);
    setPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [value]);

  let image;

  if (preview) {
    image = <img src={preview} className={styles.thumbIcon} />;
  } else if (defaultValue) {
    image = <img src={defaultValue} className={styles.thumbIcon} />;
  } else {
    image = (
      <div className={styles.thumbPlaceholder}>
        <span>No Image</span>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.thumbnail} style={{ backgroundColor }}>
        {image}
      </div>

      <Button
        onClick={(e) => {
          e.preventDefault();
          thumbnail.current.click();
        }}
      >
        {label}
      </Button>
      {fileError && (
        <span className={styles.fileError} role="alert">
          {fileError}
        </span>
      )}

      <input
        type="file"
        id="thumbnail"
        ref={thumbnail}
        className={styles.input}
        accept="image/*"
        onChange={(e) => {
          if (typeof onChange === "function") {
            if (!e.target.files || e.target.files.length === 0) {
              setFileError("");
              onChange(undefined);
              return;
            }

            const file = e.target.files[0];
            if (!file.type.startsWith("image/")) {
              setFileError("Select an image file.");
              e.target.value = "";
              return;
            }

            setFileError("");
            onChange(file);
          }
        }}
      />
    </div>
  );
}
